import { json, type LoaderArgs, type V2_MetaFunction } from "@remix-run/node";
import { useLoaderData } from "@remix-run/react";
import { fetchJobPost } from "~/utils/fetch_job_post";
import { JobPost, links as jobPostLinks } from "~/components/job_posts/job_post";
import {
  ApplicationForm,
  links as applicationFormLinks,
} from "~/components/job_posts/application_form";
import { Logo, links as logoLinks } from "~/components/logo";
import { useBoardConfiguration } from "~/hooks/use_board_configuration";
import { buildJobPostTags } from "~/utils/job_helpers";
import { useRef } from "react";
import { focusForm } from "~/utils/focus_form";
import { isClient } from "~/utils/env_check";
import { loadGooglePicker } from "~/utils/load_google_picker";
import useRecruitics from "~/hooks/use_recruitics";
import { withNamespace } from "~/utils/translation";
import config from "~/config.server";
import { useSearchParams } from "~/hooks/use_search_params";

export const links = () => [...jobPostLinks(), ...applicationFormLinks(), ...logoLinks()];

type Params = {
  url_token: string;
  job_post_id: string;
};

export const loader = async ({ params, request }: LoaderArgs) => {
  const { url_token: urlToken, job_post_id: jobPostId } = params as Params;
  const currentUrl = new URL(request.url);

  const { jobPost, status: jobApplicationStatus } = await fetchJobPost({
    urlToken,
    jobPostId,
    apiVersion: "v2",
    includeQuestions: true,
    includeEducation: true,
  });

  if (jobApplicationStatus === 404) {
    throw new Response(null, { status: 404 });
  }

  const confirmationPath = `/${urlToken}/jobs/${jobPostId}/confirmation`;

  return json({
    jobPost,
    submitPath: config.get("domains.jben_url") + currentUrl.pathname,
    confirmationPath,
  });
};

export const meta: V2_MetaFunction = ({ data }) => {
  const t = withNamespace("job_post");
  const { title, company_name: companyName } = data.jobPost;
  return [
    {
      title: t("page_title", {
        title,
        companyName,
        interpolation: { escapeValue: false },
      }),
    },
  ];
};

export const handle = { i18n: "job_post" };

let loadedGooglePicker: boolean;

export default function ExternalJobPost() {
  const { jobPost, submitPath, confirmationPath } = useLoaderData<typeof loader>();
  const {
    enable_recruitics: enableRecruitics,
    logo,
    google_drive_enabled,
    hide_app_description,
  } = useBoardConfiguration();
  const searchParams = useSearchParams();

  const formRef = useRef<HTMLFormElement>(null);

  const shouldLoadGooglePicker = isClient() && google_drive_enabled;

  if (!loadedGooglePicker && shouldLoadGooglePicker) {
    loadGooglePicker();
    loadedGooglePicker = true;
  }

  useRecruitics(enableRecruitics);

  return (
    <div className="job-post__wrapper">
      <Logo
        companyName={jobPost.company_name}
        url={logo.url}
        href={logo.href && logo.href + searchParams}
      />
      {!hide_app_description && (
        <>
          <JobPost
            content={jobPost.content}
            conclusion={jobPost.conclusion}
            introduction={jobPost.introduction}
            location={jobPost.job_post_location}
            title={jobPost.title}
            payRanges={jobPost.pay_ranges}
            tags={buildJobPostTags(jobPost)}
            onApplyClick={() => focusForm(formRef.current)}
            hiringPlanId={jobPost.hiring_plan_id}
          />
          <div className="divider" role="separator"></div>
        </>
      )}
      <ApplicationForm
        jobPost={jobPost}
        demographicSection={jobPost.demographic_questions}
        questions={jobPost.questions}
        submitPath={submitPath}
        confirmationPath={confirmationPath + searchParams}
        formRef={formRef}
      />
    </div>
  );
}
